import React, { useEffect, useState } from "react";
import { Alert, Input, Modal } from "antd";
import useLang from "../../../hooks/useLang";
import { OrderStatusHistoryRow } from "../../../api/bulk-order-operations";
import { CONFIRMATION_WORD } from "../components/ConfirmModal";

interface DeleteHistoryModalProps {
	row: OrderStatusHistoryRow | null;
	deleting: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

const DeleteHistoryModal = ({
	row,
	deleting,
	onCancel,
	onConfirm,
}: DeleteHistoryModalProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.historyManagement;

	const [confirmWord, setConfirmWord] = useState("");

	useEffect(() => {
		if (!row) setConfirmWord("");
	}, [row]);

	const confirmOk = confirmWord.trim().toUpperCase() === CONFIRMATION_WORD;

	return (
		<Modal
			open={!!row}
			title={copy?.deleteTitle ?? "Delete history row"}
			okText={copy?.delete ?? "Delete"}
			cancelText={common?.cancel ?? "Cancel"}
			okButtonProps={{
				danger: true,
				loading: deleting,
				disabled: !confirmOk,
			}}
			onOk={onConfirm}
			onCancel={onCancel}
			destroyOnClose
		>
			<div className="flex flex-col gap-2 py-2">
				<Alert
					type="error"
					showIcon
					message={
						copy?.deleteWarning ??
						"This permanently removes the row. It cannot be undone from this page."
					}
				/>

				{/*
					AddBalance stamps OrderPayment.CreatedAt from the order's most recent
					history row, so deleting that one moves a credited order into a
					different settlement period.
				*/}
				{row?.isLatest && (
					<Alert
						type="warning"
						showIcon
						message={
							copy?.deleteLatestWarning ??
							"This is the most recent history row. AddBalance uses its date as the delivered date when crediting the seller."
						}
					/>
				)}

				{row && (
					<div className="text-sm text-gray-600 font-mono break-all">
						#{row.id} · {new Date(row.statusDate).toLocaleString()}{" "}
						· {row.statusLabel ?? `#${row.statusId}`} ·{" "}
						{row.statusChangeComment ?? "—"}
					</div>
				)}

				<div className="font-medium">
					{common?.typeToConfirm ??
						`Type ${CONFIRMATION_WORD} to enable the button`}
				</div>
				<Input
					autoFocus
					value={confirmWord}
					onChange={(e) => setConfirmWord(e.target.value)}
				/>
			</div>
		</Modal>
	);
};

export default DeleteHistoryModal;
